import React, { useState, useEffect, useMemo } from 'react';
import { 
  Wallet, 
  Calendar, 
  ArrowUpRight, 
  ArrowDownRight, 
  Filter, 
  Search, 
  CreditCard, 
  BanknoteIcon, 
  ArrowDownToLine, 
  CheckSquare, 
  Landmark, 
  X,
  Plus,
  Sliders,
  Trash2
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from "../components/ui/Button";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import { useAppStore } from '../store';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import BalanceAdjustmentModal from '../components/modals/BalanceAdjustmentModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { supabase } from '../lib/supabase';

// Define payment method types
type PaymentMethod = 'cash' | 'transfer' | 'card' | 'check' | 'mercadopago' | 'all';

// Define movement types
interface Movement {
  id: string;
  date: string;
  amount: number;
  type: 'income' | 'expense' | 'adjustment';
  description: string;
  source: 'payment' | 'expense' | 'adjustment';
  reference?: string;
  entity_name?: string;
  payment_method?: string;
}

const Treasury: React.FC = () => {
  const { payments, expenses, invoices, clients, suppliers } = useAppStore();
  
  // State for selected payment method
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('all');
  
  // State for date filters
  const [dateRange, setDateRange] = useState<string>('month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // State for search query
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // State for balance adjustment modal
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState<boolean>(false);
  
  // State for balance adjustments
  const [balanceAdjustments, setBalanceAdjustments] = useState<any[]>([]);
  
  // State for delete confirmation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [adjustmentToDelete, setAdjustmentToDelete] = useState<string | null>(null);
  
  // Fetch balance adjustments
  useEffect(() => {
    const fetchBalanceAdjustments = async () => {
      try {
        // Check if the balance_adjustments table exists
        const { error: checkError } = await supabase
          .from('balance_adjustments')
          .select('count')
          .limit(1);
        
        if (checkError) {
          // Table doesn't exist yet, just return
          console.log('Balance adjustments table does not exist yet');
          return;
        }
        
        // Fetch balance adjustments
        const { data, error } = await supabase
          .from('balance_adjustments')
          .select('*')
          .order('date', { ascending: false });
        
        if (error) throw error;
        
        setBalanceAdjustments(data || []);
      } catch (error) {
        console.error('Error fetching balance adjustments:', error);
      }
    };
    
    fetchBalanceAdjustments();
  }, []);
  
  // Set default date range when component mounts or date range changes
  useEffect(() => {
    const now = new Date();
    let start = new Date();
    
    switch (dateRange) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case '7days':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'custom':
        // Don't change dates for custom range
        return;
      default:
        // 'all' - don't set date filters
        setStartDate('');
        setEndDate('');
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  }, [dateRange]);
  
  // Get entity name (client or supplier) for a payment
  const getEntityNameForPayment = (payment: any): string => {
    const invoice = invoices.find(inv => inv.id === payment.invoice_id);
    if (!invoice) return 'Desconocido';
    
    if (invoice.entity_type === 'client') {
      const client = clients.find(c => c.id === invoice.entity_id);
      return client?.name || 'Cliente desconocido';
    } else {
      const supplier = suppliers.find(s => s.id === invoice.entity_id);
      return supplier?.name || 'Proveedor desconocido';
    }
  };
  
  // Get entity name for an expense
  const getEntityNameForExpense = (expense: any): string => {
    if (!expense.supplier_id) return 'Sin proveedor';
    const supplier = suppliers.find(s => s.id === expense.supplier_id);
    return supplier?.name || 'Proveedor desconocido';
  };
  
  // Get description for a payment
  const getDescriptionForPayment = (payment: any): string => {
    const invoice = invoices.find(inv => inv.id === payment.invoice_id);
    if (!invoice) return 'Pago sin factura';
    
    return `Pago de factura ${invoice.number}`;
  };
  
  // Combine payments, expenses, and adjustments into a single list of movements
  const movements = useMemo(() => {
    // Filter out voided payments
    const validPayments = payments.filter(p => !p.voided);
    
    // Map payments to movements
    const paymentMovements: Movement[] = validPayments.map(payment => {
      const invoice = invoices.find(inv => inv.id === payment.invoice_id);
      const isIncome = invoice?.entity_type === 'client';
      
      return {
        id: payment.id,
        date: payment.date,
        amount: payment.amount,
        type: isIncome ? 'income' : 'expense',
        description: getDescriptionForPayment(payment),
        source: 'payment',
        reference: payment.reference,
        entity_name: getEntityNameForPayment(payment),
        payment_method: payment.method
      };
    });
    
    // Map expenses to movements
    const expenseMovements: Movement[] = expenses.map(expense => {
      return {
        id: expense.id,
        date: expense.date,
        amount: expense.amount,
        type: 'expense',
        description: expense.description,
        source: 'expense',
        reference: expense.reference,
        entity_name: getEntityNameForExpense(expense),
        payment_method: expense.payment_method
      };
    });
    
    // Map balance adjustments to movements
    const adjustmentMovements: Movement[] = balanceAdjustments.map(adjustment => {
      return {
        id: adjustment.id,
        date: adjustment.date,
        amount: Math.abs(adjustment.amount),
        type: adjustment.amount > 0 ? 'income' : 'expense',
        description: adjustment.description || 'Ajuste de saldo',
        source: 'adjustment',
        payment_method: adjustment.payment_method
      };
    });
    
    // Combine and return all movements
    return [...paymentMovements, ...expenseMovements, ...adjustmentMovements];
  }, [payments, expenses, invoices, clients, suppliers, balanceAdjustments]);
  
  // Filter movements by payment method, date range, and search query
  const filteredMovements = useMemo(() => {
    return movements.filter(movement => {
      // Filter by payment method
      if (selectedMethod !== 'all') {
        if (movement.source === 'payment') {
          if (movement.payment_method !== selectedMethod) return false;
        } else if (movement.source === 'expense') {
          if (movement.payment_method !== selectedMethod) return false;
        } else if (movement.source === 'adjustment') {
          if (movement.payment_method !== selectedMethod) return false;
        }
      }
      
      // Filter by date range
      if (startDate && endDate) {
        const movementDate = new Date(movement.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59); // Include the entire end day
        
        if (movementDate < start || movementDate > end) return false;
      }
      
      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          movement.description.toLowerCase().includes(query) ||
          (movement.entity_name && movement.entity_name.toLowerCase().includes(query)) ||
          (movement.reference && movement.reference.toLowerCase().includes(query))
        );
      }
      
      return true;
    });
  }, [movements, selectedMethod, startDate, endDate, searchQuery]);
  
  // Sort movements by date (most recent first)
  const sortedMovements = useMemo(() => {
    const sorted = [...filteredMovements].sort((a, b) => {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
    return sorted.reverse();
  }, [filteredMovements]);
  
  // Calculate balance for the selected payment method
  const balance = useMemo(() => {
    let totalIncome = 0;
    let totalExpense = 0;
    
    filteredMovements.forEach(movement => {
      if (movement.type === 'income') {
        totalIncome += movement.amount;
      } else {
        totalExpense += movement.amount;
      }
    });
    
    return totalIncome - totalExpense;
  }, [filteredMovements]);
  
  // Get payment method display name
  const getPaymentMethodName = (method: PaymentMethod): string => {
    switch (method) {
      case 'cash': return 'Efectivo';
      case 'transfer': return 'Transferencia';
      case 'card': return 'Tarjeta';
      case 'check': return 'Cheque';
      case 'mercadopago': return 'MercadoPago';
      case 'all': return 'Todos los medios';
      default: return method;
    }
  };
  
  // Get payment method icon
  const getPaymentMethodIcon = (method: PaymentMethod) => {
    switch (method) {
      case 'cash': return <BanknoteIcon className="h-5 w-5" />;
      case 'transfer': return <Landmark className="h-5 w-5" />;
      case 'card': return <CreditCard className="h-5 w-5" />;
      case 'check': return <CheckSquare className="h-5 w-5" />;
      case 'mercadopago': return <Wallet className="h-5 w-5" />;
      case 'all': return <Wallet className="h-5 w-5" />;
      default: return <Wallet className="h-5 w-5" />;
    }
  };
  
  // Reset all filters
  const resetFilters = () => {
    setDateRange('month');
    setSearchQuery('');
  };

  // Handle delete adjustment
  const handleDeleteAdjustment = (id: string) => {
    setAdjustmentToDelete(id);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete adjustment
  const confirmDeleteAdjustment = async () => {
    if (!adjustmentToDelete) return;

    try {
      const { error } = await supabase
        .from('balance_adjustments')
        .delete()
        .eq('id', adjustmentToDelete);

      if (error) throw error;

      // Update local state
      setBalanceAdjustments(prev => prev.filter(adjustment => adjustment.id !== adjustmentToDelete));
    } catch (error) {
      console.error('Error deleting adjustment:', error);
    } finally {
      setAdjustmentToDelete(null);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Wallet className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Tesorería
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<Sliders className="h-4 w-4" />}
            onClick={() => setIsAdjustmentModalOpen(true)}
          >
            Ajustar Saldo
          </Button>
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={resetFilters}
          >
            Limpiar Filtros
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Payment Methods Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader title="Medios de Pago" />
            <CardContent>
              <div className="space-y-2">
                <Button
                  variant={selectedMethod === 'all' ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  icon={<Wallet className="h-5 w-5" />}
                  onClick={() => setSelectedMethod('all')}
                >
                  Todos los medios
                </Button>
                <Button
                  variant={selectedMethod === 'cash' ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  icon={<BanknoteIcon className="h-5 w-5" />}
                  onClick={() => setSelectedMethod('cash')}
                >
                  Efectivo
                </Button>
                <Button
                  variant={selectedMethod === 'transfer' ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  icon={<Landmark className="h-5 w-5" />}
                  onClick={() => setSelectedMethod('transfer')}
                >
                  Transferencia
                </Button>
                <Button
                  variant={selectedMethod === 'card' ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  icon={<CreditCard className="h-5 w-5" />}
                  onClick={() => setSelectedMethod('card')}
                >
                  Tarjeta
                </Button>
                <Button
                  variant={selectedMethod === 'check' ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  icon={<CheckSquare className="h-5 w-5" />}
                  onClick={() => setSelectedMethod('check')}
                >
                  Cheque
                </Button>
                <Button
                  variant={selectedMethod === 'mercadopago' ? 'primary' : 'outline'}
                  className="w-full justify-start"
                  icon={<Wallet className="h-5 w-5" />}
                  onClick={() => setSelectedMethod('mercadopago')}
                >
                  MercadoPago
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Balance Card */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                    Saldo Actual: {getPaymentMethodName(selectedMethod)}
                  </h3>
                  <p className={`text-3xl font-bold mt-2 ${
                    balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {formatCurrency(balance)}
                  </p>
                </div>
                <div className={`p-4 rounded-full ${
                  balance >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  {getPaymentMethodIcon(selectedMethod)}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Filters */}
          <Card>
            <CardHeader title="Filtros" />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Select
                  label="Período"
                  value={dateRange}
                  onChange={setDateRange}
                  options={[
                    { value: 'all', label: 'Todo el tiempo' },
                    { value: 'today', label: 'Hoy' },
                    { value: '7days', label: 'Últimos 7 días' },
                    { value: 'month', label: 'Este mes' },
                    { value: 'custom', label: 'Personalizado' },
                  ]}
                />
                
                {dateRange === 'custom' && (
                  <>
                    <Input
                      label="Desde"
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <Input
                      label="Hasta"
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </>
                )}
              </div>
              
              <div className="relative">
                <Input
                  label="Buscar"
                  id="searchQuery"
                  name="searchQuery"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por descripción, entidad o referencia..."
                />
                <div className="absolute right-3 top-9">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Movements Table */}
          <Card>
            <CardHeader title={`Movimientos (${sortedMovements.length})`} />
            <CardContent>
              {sortedMovements.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No hay movimientos para mostrar con los filtros seleccionados
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Fecha
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Descripción
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Entidad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Referencia
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Monto
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {sortedMovements.map((movement) => (
                        <tr key={`${movement.source}-${movement.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                              {formatArgentinaDate(movement.date)}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <div className="flex items-center">
                              {movement.type === 'income' ? (
                                <ArrowDownRight className="h-4 w-4 text-emerald-500 mr-2" />
                              ) : (
                                <ArrowUpRight className="h-4 w-4 text-red-500 mr-2" />
                              )}
                              {movement.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {movement.entity_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {movement.reference || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              movement.source === 'payment' 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                : movement.source === 'expense'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                            }`}>
                              {movement.source === 'payment' 
                                ? 'Pago' 
                                : movement.source === 'expense'
                                ? 'Gasto'
                                : 'Ajuste'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                            <span className={
                              movement.type === 'income' 
                                ? 'text-emerald-600 dark:text-emerald-400' 
                                : 'text-red-600 dark:text-red-400'
                            }>
                              {movement.type === 'income' ? '+' : '-'}
                              {formatCurrency(movement.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            {movement.source === 'adjustment' && (
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => handleDeleteAdjustment(movement.id)}
                              >
                                Eliminar
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Balance Adjustment Modal */}
      <BalanceAdjustmentModal
        isOpen={isAdjustmentModalOpen}
        onClose={() => setIsAdjustmentModalOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAdjustmentToDelete(null);
        }}
        onConfirm={confirmDeleteAdjustment}
        title="Eliminar Ajuste"
        message="¿Estás seguro de que deseas eliminar este ajuste? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
    </div>
  );
};

export default Treasury;